import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Autocomplete, TextField, Card, CardContent,
  Checkbox, FormControlLabel, Switch, Chip, Tooltip, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import {
  financialStatusTranslations, getFinancialStatusColor,
} from '../../utils/statusTranslations';
import Users from '../../collections/users';
import Invoices from '../../collections/invoices';
import Invoice from '../../models/invoice';
import Products from '../../collections/products';
import Packages from '../../collections/packages';

const formatVND = (n) => `${(Number(n) || 0).toLocaleString('vi-VN')} đ`;
const LINE_TYPE_LABELS = {
  PACKAGE_FEE: 'Gói tháng', OVERAGE: 'Vượt gói', INSTANT_ITEM: 'Mua lẻ',
  EXTENSION: 'Gia hạn', SHIPPING: 'Vận chuyển', PRODUCT: 'Sản phẩm',
};
const LINE_TYPE_OPTIONS = ['PRODUCT', 'INSTANT_ITEM', 'PACKAGE_FEE', 'EXTENSION', 'OVERAGE', 'SHIPPING'];
// Package period, shown on the picker so staff can tell cycles apart at a glance.
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('vi-VN') : '?');
const packagePeriod = (p) => (
  p.beginning_date || p.ending_date
    ? ` · ${shortDate(p.beginning_date)} – ${shortDate(p.ending_date)}`
    : ''
);
const lineTotal = (l) => Math.round((Number(l.quantity) || 0) * (Number(l.unit_price) || 0));
// S22: these line types name a package, so their description comes from the catalog.
const isPackageLine = (l) => ['PACKAGE_FEE', 'EXTENSION'].includes(l.line_type);

/** Manual invoice builder (FINV-02-S08): pick a customer, select their orders/packages,
 *  preview & edit the lines, then create one combined CUSTOM invoice. */
const InvoiceBuilder = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);

  const [sources, setSources] = useState({ orders: [], user_packages: [] });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState([]);

  const [lines, setLines] = useState([]);
  // S21: renewal (gia hạn) is an explicit choice, off unless staff turn it on.
  const [includeExtension, setIncludeExtension] = useState(false);
  const [products, setProducts] = useState([]);
  const [packageCatalog, setPackageCatalog] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (setPageTitle) setPageTitle('Tạo hóa đơn');
    (async () => {
      try {
        const list = new Products();
        await list.fetch('ACTIVE');
        setProducts(list.toArray());
      } catch (e) { /* products optional */ }
      try {
        const list = new Packages();
        await list.fetch({ per_page: 100, status: 'ACTIVE' });
        setPackageCatalog(list.toArray());
      } catch (e) { /* packages optional */ }
    })();
  }, [setPageTitle]);

  // Pre-fill when navigated from an order/package detail page with a selected source.
  useEffect(() => {
    const st = location.state;
    if (!st || !st.customer) return;
    (async () => {
      setCustomer(st.customer);
      setCustomerOptions([st.customer]);
      setSources(await new Invoices().fetchSources(st.customer.id));
      const orderIds = st.preselectOrderIds || [];
      const pkgIds = st.preselectPackageIds || [];
      setSelectedOrders(orderIds);
      setSelectedPackages(pkgIds);
      if (orderIds.length || pkgIds.length) {
        try {
          const previewLines = await new Invoice().composePreview({
            user_id: st.customer.id, order_ids: orderIds,
            user_package_ids: pkgIds, include_extension: false,   // prefill never renews
          });
          setLines(previewLines.map((l) => ({ ...l })));
        } catch (e) {
          toast.error(e?.response?.data?.error || 'Không thể tải hóa đơn');
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchCustomers = async (query) => {
    if (!query) { setCustomerOptions([]); return; }
    setCustomerLoading(true);
    try {
      const users = new Users();
      await users.fetch(1, 50, {}, query);
      setCustomerOptions(users.toArray());
    } finally { setCustomerLoading(false); }
  };

  const selectCustomer = async (user) => {
    setCustomer(user);
    setSources({ orders: [], user_packages: [] });
    setSelectedOrders([]); setSelectedPackages([]); setLines([]);
    if (user) setSources(await new Invoices().fetchSources(user.id));
  };

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const loadPreview = async () => {
    if (!selectedOrders.length && !selectedPackages.length) {
      toast.warn('Chọn ít nhất một đơn hàng hoặc gói dịch vụ'); return;
    }
    setPreviewing(true);
    try {
      const previewLines = await new Invoice().composePreview({
        user_id: customer.id,
        order_ids: selectedOrders,
        user_package_ids: selectedPackages,
        include_extension: includeExtension,
      });
      // Recompute the source lines but keep the hand-written ones (S22) — the button
      // adds to the invoice, it should not throw away rows staff typed themselves.
      setLines((prev) => [
        ...previewLines.map((l) => ({ ...l })),
        ...prev.filter((l) => l._manual),
      ]);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Không thể xem trước hóa đơn');
    } finally { setPreviewing(false); }
  };

  // Discount: reduce the unit price of every line of a chosen type (or all).
  const [discountType, setDiscountType] = useState('ALL');
  const [discountPercent, setDiscountPercent] = useState('');

  const applyDiscount = () => {
    const pct = Number(discountPercent);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toast.warn('Nhập phần trăm chiết khấu từ 1 đến 100');
      return;
    }
    const targets = lines.filter((l) =>
      discountType === 'ALL' || (l.line_type || 'PRODUCT') === discountType);
    if (!targets.length) {
      toast.warn('Không có dòng nào thuộc loại đã chọn');
      return;
    }
    setLines((prev) => prev.map((l) => (
      discountType === 'ALL' || (l.line_type || 'PRODUCT') === discountType
        ? { ...l, unit_price: Math.round((Number(l.unit_price) || 0) * (1 - pct / 100)) }
        : l
    )));
    toast.success(`Đã giảm ${pct}% cho ${targets.length} dòng`);
  };

  const updateLine = (i, field, value) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));
  // `_manual` is client-only (submit maps the fields it sends explicitly): it marks the
  // rows staff typed, so re-previewing the sources does not wipe them.
  const addLine = () => setLines((prev) => [...prev, {
    description: '', line_type: 'PRODUCT', quantity: 1, unit: 'kg', unit_price: 0,
    _manual: true,
  }]);
  const addPackageLine = () => setLines((prev) => [...prev, {
    description: '', line_type: 'PACKAGE_FEE', quantity: 1, unit: 'gói', unit_price: 0,
    _manual: true,
  }]);
  const selectProduct = (i, product) => {
    if (!product || typeof product !== 'object') return;
    setLines((prev) => prev.map((l, idx) => (idx === i
      ? { ...l, description: product.name, unit_price: product.latest_price || 0, product_id: product.id }
      : l)));
  };

  // Fill the line the way the server writes a package fee: name in quotes, one "gói".
  const selectPackage = (i, pkg) => {
    if (!pkg || typeof pkg !== 'object') return;
    setLines((prev) => prev.map((l, idx) => (idx === i
      ? {
        ...l,
        description: `${l.line_type === 'EXTENSION' ? 'Gia hạn gói' : 'Gói tháng'} "${pkg.name}"`,
        quantity: 1, unit: 'gói', unit_price: pkg.price || 0,
      }
      : l)));
  };

  const total = lines.reduce((s, l) => s + lineTotal(l), 0);

  const submit = async () => {
    if (!lines.length) { toast.warn('Hóa đơn chưa có dòng nào'); return; }
    if (lines.some((l) => !String(l.description || '').trim())) {
      toast.warn('Có dòng chưa điền mô tả — vui lòng điền hoặc xóa trước khi tạo hóa đơn');
      return;
    }
    if (lines.some((l) => !(Number(l.quantity) > 0))) {
      toast.warn('Có dòng số lượng 0 — vui lòng cập nhật hoặc xóa trước khi tạo hóa đơn');
      return;
    }
    setSubmitting(true);
    try {
      const created = await new Invoice().compose({
        user_id: customer.id,
        order_ids: selectedOrders,
        user_package_ids: selectedPackages,
        line_items: lines.map((l) => ({
          description: l.description,
          line_type: l.line_type || 'PRODUCT',
          quantity: Number(l.quantity) || 0,
          unit: l.unit || 'kg',
          unit_price: Number(l.unit_price) || 0,
          total_price: lineTotal(l),
          product_id: l.product_id || null,
          order_line_item_id: l.order_line_item_id || null,
        })),
      });
      if (created) {
        toast.success(`Đã tạo hóa đơn ${created.code}`);
        navigate(`/invoices/${created.id}`);
      } else {
        toast.error('Không thể tạo hóa đơn');
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Lỗi khi tạo hóa đơn');
    } finally { setSubmitting(false); }
  };

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Tạo hóa đơn</Typography>

      <Autocomplete
        options={customerOptions}
        getOptionLabel={(o) => (o.name ? `${o.name}${o.phone_number ? ' - ' + o.phone_number : ''}` : '')}
        filterOptions={(x) => x}
        loading={customerLoading}
        value={customer}
        onChange={(e, v) => selectCustomer(v)}
        onInputChange={(e, v) => searchCustomers(v)}
        renderInput={(params) => (
          <TextField {...params} label="Khách hàng" placeholder="Tìm khách hàng..." />
        )}
        sx={{ maxWidth: 480, mb: 3 }}
      />

      {customer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Chọn đơn hàng & gói dịch vụ</Typography>
            <FormControlLabel
              control={(
                <Switch checked={includeExtension}
                  onChange={(e) => setIncludeExtension(e.target.checked)} />
              )}
              label="Gia hạn gói (thêm phí gói mới vào hóa đơn)"
            />
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 1 }}>
              <Box sx={{ minWidth: 280 }}>
                <Typography variant="subtitle2">Đơn mua lẻ</Typography>
                {sources.orders.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Không có đơn đủ điều kiện</Typography>
                )}
                {sources.orders.map((o) => (
                  <Box key={o.id}>
                    <FormControlLabel
                      control={<Checkbox checked={selectedOrders.includes(o.id)} onChange={() => toggle(selectedOrders, setSelectedOrders, o.id)} />}
                      label={`${o.name || ('#' + o.id)} — ${formatVND(o.total_price)}`}
                    />
                  </Box>
                ))}
              </Box>
              <Box sx={{ minWidth: 280 }}>
                <Typography variant="subtitle2">Gói dịch vụ (chưa thanh toán)</Typography>
                {sources.user_packages.length === 0 && (
                  <Typography variant="body2" color="text.secondary">Không có gói đủ điều kiện</Typography>
                )}
                {sources.user_packages.map((p) => (
                  <Box key={p.id}>
                    <FormControlLabel
                      control={<Checkbox checked={selectedPackages.includes(p.id)} onChange={() => toggle(selectedPackages, setSelectedPackages, p.id)} />}
                      label={(
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <span>{`${p.package_name || ('Gói #' + p.id)} (#${p.id})${packagePeriod(p)}`}</span>
                          <Chip size="small"
                            label={financialStatusTranslations[p.financial_status] || p.financial_status}
                            color={getFinancialStatusColor(p.financial_status)} />
                          {/* S20: this package was invoiced before; picking it now bills
                              only the instant items delivered since. */}
                          {p.already_invoiced && (
                            <Tooltip title="Gói đã có hóa đơn trước đó — hóa đơn này chỉ tính các mục mua lẻ phát sinh sau">
                              <Chip size="small" variant="outlined" color="info"
                                label="Hóa đơn bổ sung" />
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={loadPreview} disabled={previewing}>
              {previewing ? 'Đang tải...' : 'Thêm vào hóa đơn'}
            </Button>
          </CardContent>
        </Card>
      )}

      {customer && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Chi tiết hóa đơn</Typography>

            {/* Chiết khấu: giảm đơn giá của các dòng theo loại */}
            {lines.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
                       mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Chiết khấu
              </Typography>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="discount-type-label">Áp dụng cho</InputLabel>
                <Select labelId="discount-type-label" label="Áp dụng cho"
                  value={discountType} onChange={(e) => setDiscountType(e.target.value)}>
                  <MenuItem value="ALL">Tất cả các dòng</MenuItem>
                  {LINE_TYPE_OPTIONS.map((t) => (
                    <MenuItem key={t} value={t}>{LINE_TYPE_LABELS[t]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Phần trăm (%)" sx={{ width: 130 }}
                value={discountPercent} inputProps={{ min: 1, max: 100 }}
                onChange={(e) => setDiscountPercent(e.target.value)} />
              <Button variant="outlined" size="medium" onClick={applyDiscount}>
                Áp dụng
              </Button>
            </Box>
            )}

            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Loại</TableCell>
                  <TableCell sx={{ minWidth: 260 }}>Mô tả</TableCell>
                  <TableCell align="right">SL</TableCell>
                  <TableCell align="right">Đơn giá</TableCell>
                  <TableCell align="right">Thành tiền</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center"
                      sx={{ py: 3, color: 'text.secondary' }}>
                      Chưa có dòng nào — chọn nguồn ở trên, hoặc thêm dòng bên dưới.
                    </TableCell>
                  </TableRow>
                )}
                {lines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Select variant="standard" value={l.line_type || 'PRODUCT'}
                        onChange={(e) => updateLine(i, 'line_type', e.target.value)} sx={{ minWidth: 110 }}>
                        {LINE_TYPE_OPTIONS.map((t) => (
                          <MenuItem key={t} value={t}>{LINE_TYPE_LABELS[t]}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      {l.line_type === 'PRODUCT' ? (
                        <Autocomplete
                          freeSolo
                          options={products}
                          getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                          inputValue={l.description || ''}
                          onInputChange={(e, v, reason) => {
                            if (reason === 'input' || reason === 'clear') updateLine(i, 'description', v);
                          }}
                          onChange={(e, v) => selectProduct(i, v)}
                          renderInput={(params) => (
                            <TextField {...params} variant="standard" placeholder="Tìm sản phẩm..." />
                          )}
                        />
                      ) : isPackageLine(l) ? (
                        <Autocomplete
                          freeSolo
                          options={packageCatalog}
                          getOptionLabel={(o) => (typeof o === 'string' ? o : o.name || '')}
                          slotProps={{ popper: { style: { width: 'fit-content', minWidth: 300 } } }}
                          renderOption={(props, o) => (
                            <li {...props} key={o.id} style={{ whiteSpace: 'nowrap' }}>
                              {`${o.name} — ${formatVND(o.price)}`}
                            </li>
                          )}
                          inputValue={l.description || ''}
                          onInputChange={(e, v, reason) => {
                            if (reason === 'input' || reason === 'clear') updateLine(i, 'description', v);
                          }}
                          onChange={(e, v) => selectPackage(i, v)}
                          renderInput={(params) => (
                            <TextField {...params} variant="standard" placeholder="Chọn gói dịch vụ..." />
                          )}
                        />
                      ) : (
                        <TextField variant="standard" value={l.description}
                          onChange={(e) => updateLine(i, 'description', e.target.value)} fullWidth />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <TextField variant="standard" type="number" value={l.quantity}
                        onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        sx={{ width: 70 }} inputProps={{ style: { textAlign: 'right' } }} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField variant="standard" type="number" value={l.unit_price}
                        onChange={(e) => updateLine(i, 'unit_price', e.target.value)}
                        sx={{ width: 110 }} inputProps={{ style: { textAlign: 'right' } }} />
                    </TableCell>
                    <TableCell align="right">{formatVND(lineTotal(l))}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeLine(i)} aria-label="delete">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {lines.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="right"><strong>Tổng cộng</strong></TableCell>
                    <TableCell align="right"><strong>{formatVND(total)}</strong></TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
            </Box>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button startIcon={<AddIcon />} onClick={addLine}>Thêm dòng</Button>
              <Button startIcon={<AddIcon />} onClick={addPackageLine}>Thêm gói dịch vụ</Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" onClick={submit}
                disabled={submitting || lines.length === 0}>
                {submitting ? 'Đang tạo...' : 'Tạo hóa đơn'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default InvoiceBuilder;
